'use client';

import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';

import { useT } from '@/hooks';

import type { ProfileSection } from '@/constants';
import { PROFILE_SIDEBAR_ITEMS } from '@/constants';
export type { ProfileSection } from '@/constants';

interface ProfileSidebarItemProps {
  item: { label: string; value: ProfileSection };
  isActive: boolean;
  onSectionChange: (section: ProfileSection) => void;
}

function ProfileSidebarItem({ item, isActive, onSectionChange }: ProfileSidebarItemProps) {
  const { t } = useT();
  const handleClick = () => {
    onSectionChange(item.value);
  };

  return (
    <ListItem
      disablePadding
      sx={{
        flex: { xs: '1 1 0', md: 'none' },
        minWidth: { xs: 0, md: '100%' },
        flexShrink: { xs: 1, md: 1 },
        width: { xs: 'auto', md: '100%' },
      }}
    >
      <ListItemButton
        onClick={handleClick}
        selected={isActive}
        sx={{
          width: '100%',
          px: { xs: 1, sm: 2, md: 2 },
          py: { xs: 1.25, sm: 1.5 },
          minHeight: 44,
          whiteSpace: 'nowrap',
          borderRadius: { xs: 1.5, md: 0 },
          mx: { xs: 0.25, md: 0 },
          justifyContent: { xs: 'center', md: 'flex-start' },
          '&.Mui-selected': {
            bgcolor: 'action.selected',
            '&:hover': { bgcolor: 'action.selected' },
          },
        }}
      >
        <ListItemText
          primary={t(item.label)}
          primaryTypographyProps={{
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            fontWeight: isActive ? 600 : 400,
            noWrap: true,
            textAlign: { xs: 'center', md: 'left' },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

interface ProfileSidebarProps {
  activeSection: ProfileSection;
  onSectionChange: (section: ProfileSection) => void;
  isLoading?: boolean;
}

export function ProfileSidebar({
  activeSection,
  onSectionChange,
  isLoading = false,
}: ProfileSidebarProps) {
  const { t } = useT();
  const renderProfileSidebarItem = (item: { label: string; value: ProfileSection }) => (
    <ProfileSidebarItem
      key={item.value}
      item={item}
      isActive={activeSection === item.value}
      onSectionChange={onSectionChange}
    />
  );

  const renderSkeletons = Array.from({ length: 3 }).map((_, i) => (
    <ListItem
      key={`sidebar-skeleton-${i}`}
      disablePadding
      sx={{
        flex: { xs: '1 1 0', md: 'none' },
        minWidth: { xs: 0, md: '100%' },
        flexShrink: { xs: 1, md: 1 },
        width: { xs: 'auto', md: '100%' },
        px: { xs: 1, sm: 2, md: 2 },
        py: { xs: 1.25, sm: 1.5 },
      }}
    >
      <Skeleton
        variant="rounded"
        width="100%"
        height={24}
        sx={{ borderRadius: 1, opacity: 0.6 }}
        animation="wave"
      />
    </ListItem>
  ));

  const renderProfileSidebar = isLoading
    ? renderSkeletons
    : PROFILE_SIDEBAR_ITEMS.map(renderProfileSidebarItem);

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: '100%', md: 240 },
        flexShrink: 0,
        alignSelf: { xs: 'stretch', md: 'flex-start' },
        height: { md: 'fit-content' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: { xs: 'visible', md: 'hidden' },
        position: { xs: 'sticky', md: 'static' },
        top: { xs: 56, sm: 60 },
        zIndex: { xs: 10, md: 'auto' },
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          color: 'text.primary',
          fontWeight: 700,
          fontSize: '0.9375rem',
          letterSpacing: '0.04em',
          display: { xs: 'none', md: 'flex' },
          borderBottom: '2px solid',
          borderColor: 'divider',
          minHeight: 48,
          alignItems: 'center',
        }}
      >
        {isLoading ? (
          <Skeleton variant="text" width={80} height={24} animation="wave" />
        ) : (
          t('nav.settings')
        )}
      </Typography>
      <List
        sx={{
          p: 0,
          display: { xs: 'flex', md: 'block' },
          flexDirection: { xs: 'row', md: 'column' },
          width: '100%',
          overflowX: { xs: 'hidden', md: 'visible' },
          overflowY: 'hidden',
          gap: 0,
          px: { xs: 0.5, md: 0 },
          py: { xs: 0.5, md: 0 },
        }}
      >
        {renderProfileSidebar}
      </List>
    </Paper>
  );
}
